<template>
	<div class="notice-detail-container">
		<DcHeader :title="headerTitle" />
		<div class="main">
			<div class="content-box" v-if="infoMain">
				<h1 class="page-title">{{ infoMain.title }}</h1>
				<div class="content-info">
					<span v-if="infoMain.publistTime || infoMain.updateTime">
						发布时间/更新时间：{{ infoMain.publistTime || infoMain.updateTime }}
					</span>
					<span v-if="infoMain.websiteName">发布人：{{ infoMain.websiteName }}</span>
					<span v-if="infoMain.publishSource || infoMain.sourceContent">来源：{{ infoMain.publishSource || infoMain.sourceContent }}</span>
				</div>

				<div class="message-content" v-html="detailContent"></div>

				<div v-if="fileList.length">
					<div class="file-preview-item" v-for="item in fileList" :key="item.url">
						<iframe
							class="page-iframe"
							v-if="item.url"
							:src="getPreviewUrl(item.url)"
							frameborder="0"
							style="height: 80vh; width: 100vw"
						></iframe>
					</div>
				</div>

				<div class="file-list" v-if="fileList.length">
					<span class="file-title">附件：</span>
					<div class="file-item" v-for="(item, index) in fileList" :key="index" @click="downloadFile(item)">
						<van-icon name="description" class="file-icon" />
						<span class="file-name">{{ item.name }}</span>
					</div>
				</div>
			</div>

			<div class="no-data" v-else>
				<span>暂无数据</span>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useHead, useRuntimeConfig, useSeoMeta } from '#imports';
import DcHeader from '@/components/DcHeader.vue';
import { getDetailAnnouncement } from '@/api/notice.js';
import { queryWebsiteByCompanyId } from '@/api/index.js';
import { buildLoginHref } from '@/utils/link/noticeDetail';
import { isGxzzFromWebsiteData } from '@/utils/business/siteGxzz.js';
import { Base64 } from 'js-base64';

const SHARE_IMAGE = 'https://uat-cmdc.oss-cn-beijing.aliyuncs.com/productFile/2022/08/18/9af2756f-27bf-4d1e-b0d1-8397e82bb2a0.png';
const PAGE_HEADER_TITLE = { hot: '国械头条', quantity: '质量公告' };

const route = useRoute();

const props = defineProps({
	/** 兜底类型：详情数据未就绪时才用到 */
	pageType: {
		type: String,
		default: 'auto',
	},
	noticeId: {
		type: [String, Number],
		default: '',
	},
});

/** 公告 ID：noticeId 优先，兜底取路由参数（去掉 .html 后缀） */
const id = computed(() => {
	const fromProps = String(props.noticeId ?? '').trim();
	return (fromProps || String(route.params.id ?? '')).replace(/\.html$/i, '');
});

/** 子站详情路径 /{companyId}/notice/{id}.html 中的 companyId（1 视为总站） */
const subsiteCompanyId = computed(() => {
	const matched = String(route.path).match(/^\/(\d+)\/notice\//);
	return matched && Number(matched[1]) !== 1 ? matched[1] : '';
});

const infoMain = ref({});
const fileList = ref([]);
/** 子站站点信息（TDK 用子站全称） */
const siteInfo = ref({});

/** 详情 + 子站站点信息并行拉取，SSR 一次、客户端水合复用 payload */
const { data: pageData } = await useAsyncData(
	() => `notice-detail-${id.value || 'none'}`,
	async () => {
		// 返回/前进时路由参数会短暂清空，无 id 不发请求
		const noticeId = id.value;
		if (!noticeId) return { detail: null, site: null };

		const siteId = subsiteCompanyId.value;
		const [detail, site] = await Promise.all([
			getDetailAnnouncement({ announcementId: noticeId }).catch(() => null),
			siteId ? queryWebsiteByCompanyId(siteId).catch(() => null) : null,
		]);
		return { detail, site };
	}
);

/** 只在有效数据时覆盖，避免请求失败/为空把已渲染内容清成「暂无数据」 */
watch(
	pageData,
	payload => {
		if (payload?.detail?.code == 200 && payload.detail.data) {
			infoMain.value = payload.detail.data;
			if (infoMain.value.fileUrl) parseFileList(infoMain.value.fileUrl);
		}
		if (payload?.site?.data) siteInfo.value = payload.site.data;
	},
	{ immediate: true }
);

/** 子站全称：fullName → companyName → websiteName */
const siteFullName = computed(() => siteInfo.value?.fullName || siteInfo.value?.companyName || siteInfo.value?.websiteName || '');
const isGxzzSite = computed(() => isGxzzFromWebsiteData(siteInfo.value));

/** 登录态用 cookie：SSR 与客户端一致 */
const tokenCookie = useCookie('cmdc_access_token_mobile');

/** 类型：以数据 noticeType 为准（4=头条，否则质量公告），未就绪时才用 prop 兜底 */
const noticeKind = computed(() => {
	const type = infoMain.value?.noticeType;
	if (type !== undefined && type !== null && type !== '') return Number(type) === 4 ? 'hot' : 'quantity';
	return props.pageType === 'hot' || props.pageType === 'quantity' ? props.pageType : '';
});
const isQuantity = computed(() => noticeKind.value === 'quantity');
const pageTypeName = computed(() => (isQuantity.value ? '质量公告' : noticeKind.value === 'hot' ? '国械在线头条' : '资讯'));
const headerTitle = computed(() => PAGE_HEADER_TITLE[noticeKind.value] || '资讯详情');

// 正文为 CMS 富文本，原样输出
const detailContent = computed(() => infoMain.value?.fileContent || '');

// 质量公告的标题/关键词带发布时间，头条不带
const noticeTitle = computed(() => infoMain.value?.title || '');
const publishTime = computed(() => infoMain.value?.publistTime || infoMain.value?.updateTime || infoMain.value?.publishTime || '');
const titleWithTime = computed(() => (isQuantity.value ? `${noticeTitle.value}${publishTime.value}` : noticeTitle.value));

/** 子站头条：TDK 用子站全称 */
const isSubsiteHeadline = computed(() => !!subsiteCompanyId.value && noticeKind.value === 'hot');

const pageTitle = computed(() => {
	if (isSubsiteHeadline.value) return [noticeTitle.value || pageTypeName.value, siteFullName.value].filter(Boolean).join(' - ');
	return [titleWithTime.value, pageTypeName.value, '国械在线'].filter(Boolean).join(' - ');
});

/** 正文首段纯文本（description 取前 80 字） */
function getFirstParagraphText(html) {
	const raw = String(html || '');
	const matched = raw.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
	return (matched ? matched[1] : raw)
		.replace(/<[^>]+>/g, '')
		.replace(/&nbsp;/gi, ' ')
		.replace(/&amp;/gi, '&')
		.replace(/\s+/g, ' ')
		.trim();
}

const pageDescription = computed(() => {
	const text = getFirstParagraphText(infoMain.value?.fileContent);
	if (text) return text.substring(0, 80);
	return `${pageTypeName.value}信息，国械在线提供专业的${pageTypeName.value}服务。`;
});

const pageKeywords = computed(() => {
	if (isSubsiteHeadline.value) return [noticeTitle.value, siteFullName.value].filter(Boolean).join(',');
	return [titleWithTime.value, pageTypeName.value, '国械在线'].filter(Boolean).join(',');
});

useSeoMeta({
	title: pageTitle,
	ogTitle: pageTitle,
	description: pageDescription,
	ogDescription: pageDescription,
	keywords: pageKeywords,
	ogImage: SHARE_IMAGE,
});

// canonical：子站 /{companyId}/notice/{id}.html，总站 /notice/{id}.html
const config = useRuntimeConfig();
const pcBase = (config.public.publicPcUrl || '').replace(/\/$/, '');
const canonicalHref = computed(() => {
	if (!pcBase) return '';
	const path = subsiteCompanyId.value ? `/${subsiteCompanyId.value}/notice/${id.value}.html` : `/notice/${id.value}.html`;
	return `${pcBase}${path}`;
});
useHead(() => ({
	meta: [
		{ name: 'applicable-device', content: 'mobile' },
		{ 'http-equiv': 'Cache-Control', content: 'no-siteapp' },
		{ 'http-equiv': 'Cache-Control', content: 'no-transform' },
	],
	link: canonicalHref.value ? [{ rel: 'canonical', href: canonicalHref.value, key: 'canonical' }] : [],
}));

/** 详情是否已返回（区分「参数未就绪」与「请求失败」，避免未就绪时误跳登录页） */
const detailLoaded = computed(() => !!pageData.value?.detail);

/** 子站详情登录校验：总站不校验；国械智造头条按 needLogin；其它子站必须登录 */
const needLoginForDetail = computed(() => {
	if (!subsiteCompanyId.value || !detailLoaded.value) return false;
	if (isGxzzSite.value && noticeKind.value === 'hot') return Number(infoMain.value?.needLogin) === 1;
	return true;
});

if (needLoginForDetail.value && !tokenCookie.value) {
	navigateTo(buildLoginHref(route.fullPath), { replace: true });
}

function parseFileList(fileUrl) {
	try {
		const fileData = JSON.parse(fileUrl);
		const fileListArray = Array.isArray(fileData) ? fileData : [fileData];
		fileList.value = fileListArray
			.map(item => ({
				name: item.name,
				url: item.response?.data || item.url,
			}))
			.filter(item => item.url);
	} catch {
		fileList.value = [];
	}
}
function getPreviewUrl(
	url,
	params = {
		watermarkTxt: '',
	}
) {
	let previewServerUrl = '';
	if (process.env.NODE_ENV == 'test' || process.env.NODE_ENV == 'development') {
		previewServerUrl = 'https://beta.cmic.com.cn/fileview/onlinePreview';
	} else {
		previewServerUrl = 'https://gyqxmall.com/fileview/onlinePreview';
	}
	const encodedUrl = Base64.encode(url);
	const queryParams = new URLSearchParams({
		url: encodedUrl,
		...params,
	}).toString();
	return `${previewServerUrl}?${queryParams}`;
}

function downloadFile(item) {
	if (item.url) {
		const previewUrl = getPreviewUrl(item.url);
		const encodedUrl = encodeURIComponent(previewUrl);
		const fileName = item.name || '文件预览';
		navigateTo(`/preview?iframeUrl=${encodedUrl}&fileName=${encodeURIComponent(fileName)}`);
	} else {
		showToast({
			message: '文件链接无效',
			type: 'none',
		});
	}
}

onMounted(() => {
	useWeixin().setShareInfo({
		title: `国械在线-${headerTitle.value}`,
		desc: `我正在看【国械在线-${headerTitle.value}】，分享给你啦。`,
		img: SHARE_IMAGE,
	});
});
</script>

<style lang="scss" scoped>
.notice-detail-container {
	min-height: 100vh;
	background-color: #f5f5f5;
}

.main {
	min-height: calc(100vh - 3.84rem);
	background-color: #f5f5f5;
	padding-bottom: 2rem;
}

.content-box {
	margin: 0.5rem 0;
	padding: 1.25rem;
	background: #fff;
	border-radius: 0.5rem;

	.page-title {
		margin-top: 10px;
		font-size: 22px;
		font-family: NotoSansSC-Medium, NotoSansSC;
		font-weight: 500;
		color: #222;
		line-height: 32px;
		text-align: center;
		margin-bottom: 1rem;
	}

	.content-info {
		margin: 0.75rem 0;
		margin-top: 20px;
		padding: 8px 16px;
		background-color: #e6ebfa;
		border-radius: 0.25rem;
		font-size: 13px;
		color: #3d3d3d;
		font-weight: 500;
		text-align: left;
		line-height: 1.6;

		span {
			display: block;
			margin-bottom: 0.25rem;
		}
	}

	.message-content {
		margin-top: 1.5rem;
		color: #1f1f1f;
		line-height: 24px;
		font-size: 14px;

		:deep(img) {
			max-width: 100%;
			height: auto;
		}

		:deep(p) {
			margin: 0.5rem 0;
		}
	}
}

.file-list {
	margin-top: 1.5rem;
	padding-top: 1rem;
	border-top: 1px solid #eee;
	overflow: visible;
	.file-title {
		font-size: 0.875rem;
		color: #666;
		display: block;
		margin-bottom: 0.5rem;
	}

	.file-item {
		display: flex;
		align-items: center;
		padding: 0.5rem;
		margin-bottom: 0.5rem;
		background: #f8f9fa;
		border-radius: 0.25rem;
		cursor: pointer;

		.file-icon {
			color: #46539d;
			margin-right: 0.5rem;
			font-size: 1.2rem;
		}

		.file-name {
			color: #46539d;
			font-size: 0.875rem;
			flex: 1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}
}

.no-data {
	padding: 3rem;
	text-align: center;
	color: #999;
	font-size: 1rem;
}
</style>
